import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { TourOption } from '~/hooks/apis/tours/useTourSearch';
import { useCreateService } from '~/hooks/apis/tours/useTourSuppliers';

import { TourService } from '~/types/tours/costPrice';
import {
  SERVICE_COST_PRICE_SCOPES,
  SERVICE_COST_PRICE_SCOPE_LABELS,
  SERVICE_COST_PRICE_TYPES,
  SERVICE_COST_PRICE_TYPE_LABELS,
  SERVICE_KINDS,
  ServiceCostPriceScope,
  ServiceCostPriceType,
  ServiceKind,
} from '~/types/tours/supplier';

import TourPicker from './TourPicker';

interface Props {
  open: boolean;
  supplierId: string;
  /** Fixed when opened from a tour, searched for when opened from a supplier. */
  tour?: TourOption;
  onClose: () => void;
  onCreated?: (service: TourService) => void;
}

interface PackageRow {
  name: string;
}

interface ConfigurationRow {
  name: string;
  packages: Array<PackageRow>;
}

function emptyConfiguration(): ConfigurationRow {
  return { name: '', packages: [{ name: '' }] };
}

/**
 * Creating the service is what links the tour to the supplier. Configurations
 * are optional, but a service without them cannot be priced.
 */
export default function ServiceFormDialog({ open, supplierId, tour, onClose, onCreated }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState('');
  const [kind, setKind] = useState<ServiceKind>('Tour');

  // svc-tour's own defaults, so an untouched form creates what it did before.
  const [costPriceType, setCostPriceType] = useState<ServiceCostPriceType>('SlidingScale');
  const [costPriceScope, setCostPriceScope] = useState<ServiceCostPriceScope>('Global');
  const [selectedTour, setSelectedTour] = useState<TourOption | null>(tour ?? null);
  const [configurations, setConfigurations] = useState<Array<ConfigurationRow>>([emptyConfiguration()]);

  const createService = useCreateService(supplierId);

  const tourId = tour?.id ?? selectedTour?.id;

  const updateConfiguration = useCallback((index: number, patch: Partial<ConfigurationRow>) => {
    setConfigurations((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  }, []);

  const updatePackage = useCallback((configurationIndex: number, packageIndex: number, packageName: string) => {
    setConfigurations((current) =>
      current.map((configuration, index) =>
        index === configurationIndex
          ? {
              ...configuration,
              packages: configuration.packages.map((row, rowIndex) =>
                rowIndex === packageIndex ? { name: packageName } : row,
              ),
            }
          : configuration,
      ),
    );
  }, []);

  // Unnamed rows are dropped: a cost price has nothing to match them on.
  const payloadConfigurations = useMemo(
    () =>
      configurations
        .filter((configuration) => configuration.name.trim())
        .map((configuration, index) => ({
          name: configuration.name.trim(),
          sequenceNumber: index + 1,
          inclusions: [],
          packages: configuration.packages
            .filter((row) => row.name.trim())
            .map((row, packageIndex) => ({
              name: row.name.trim(),
              sequenceNumber: packageIndex + 1,
              inclusions: [],
            })),
        })),
    [configurations],
  );

  /** Configuration names key the cost price rows, so they must be unique. */
  const hasDuplicateConfiguration = useMemo(() => {
    const names = payloadConfigurations.map((configuration) => configuration.name.toLowerCase());
    return new Set(names).size !== names.length;
  }, [payloadConfigurations]);

  const hasEmptyPackages = payloadConfigurations.some((configuration) => !configuration.packages.length);

  const isValid = !!name.trim() && !!tourId && !hasDuplicateConfiguration && !hasEmptyPackages;

  const handleClose = useCallback(() => {
    setName('');
    setKind('Tour');
    setCostPriceType('SlidingScale');
    setCostPriceScope('Global');
    setSelectedTour(tour ?? null);
    setConfigurations([emptyConfiguration()]);
    createService.reset();
    onClose();
  }, [createService, onClose, tour]);

  const handleSave = useCallback(async () => {
    try {
      const service = await createService.mutateAsync({
        tourId,
        name: name.trim(),
        kind,
        costPriceType,
        costPriceScope,
        ...(payloadConfigurations.length ? { configurations: payloadConfigurations } : {}),
      });

      enqueueSnackbar(`Created service ${service.name}`, { variant: 'success' });
      onCreated?.(service);
      handleClose();
    } catch {
      // The mutation error is shown in the dialog.
    }
  }, [
    costPriceScope,
    costPriceType,
    createService,
    enqueueSnackbar,
    handleClose,
    kind,
    name,
    onCreated,
    payloadConfigurations,
    tourId,
  ]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Create service</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {tour ? (
            <TextField label="Tour" value={tour.name} disabled fullWidth helperText="The tour this service is for" />
          ) : (
            <TourPicker
              value={selectedTour}
              onChange={setSelectedTour}
              helperText="The tour this service supplies. Creating the service links the tour to this supplier."
            />
          )}

          <TextField label="Service name" value={name} onChange={(event) => setName(event.target.value)} fullWidth />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Kind"
              value={kind}
              onChange={(event) => setKind(event.target.value as ServiceKind)}
              fullWidth
            >
              {SERVICE_KINDS.map((serviceKind) => (
                <MenuItem key={serviceKind} value={serviceKind}>
                  {serviceKind}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Cost price type"
              value={costPriceType}
              onChange={(event) => setCostPriceType(event.target.value as ServiceCostPriceType)}
              helperText="How the supplier quotes this service"
              fullWidth
            >
              {SERVICE_COST_PRICE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {SERVICE_COST_PRICE_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Cost price scope"
              value={costPriceScope}
              onChange={(event) => setCostPriceScope(event.target.value as ServiceCostPriceScope)}
              helperText="Whether one price covers the tour or each departure is priced"
              fullWidth
            >
              {SERVICE_COST_PRICE_SCOPES.map((scope) => (
                <MenuItem key={scope} value={scope}>
                  {SERVICE_COST_PRICE_SCOPE_LABELS[scope]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Divider />

          <Stack spacing={0.5}>
            <Typography variant="h4" fontWeight={600}>
              Configurations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cost prices are set per package option, so a service needs at least one configuration with a package
              before it can be priced.
            </Typography>
          </Stack>

          {configurations.map((configuration, configurationIndex) => (
            <Box key={configurationIndex} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    label="Configuration name"
                    value={configuration.name}
                    onChange={(event) => updateConfiguration(configurationIndex, { name: event.target.value })}
                    fullWidth
                  />
                  <IconButton
                    aria-label="Remove configuration"
                    onClick={() =>
                      setConfigurations((current) => current.filter((_row, index) => index !== configurationIndex))
                    }
                    disabled={configurations.length === 1}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>

                {configuration.packages.map((packageRow, packageIndex) => (
                  <Stack key={packageIndex} direction="row" spacing={1} alignItems="center" pl={2}>
                    <TextField
                      label="Package option"
                      value={packageRow.name}
                      onChange={(event) => updatePackage(configurationIndex, packageIndex, event.target.value)}
                      fullWidth
                    />
                    <IconButton
                      aria-label="Remove package option"
                      onClick={() =>
                        updateConfiguration(configurationIndex, {
                          packages: configuration.packages.filter((_row, index) => index !== packageIndex),
                        })
                      }
                      disabled={configuration.packages.length === 1}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                ))}

                <Box pl={2}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      updateConfiguration(configurationIndex, {
                        packages: configuration.packages.concat({ name: '' }),
                      })
                    }
                  >
                    Add package option
                  </Button>
                </Box>
              </Stack>
            </Box>
          ))}

          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setConfigurations((current) => current.concat(emptyConfiguration()))}
            >
              Add configuration
            </Button>
          </Box>

          {hasDuplicateConfiguration && (
            <Alert severity="warning">
              Configuration names must be unique - cost price rows are matched to a configuration by name.
            </Alert>
          )}

          {hasEmptyPackages && (
            <Alert severity="warning">Every named configuration needs at least one named package option.</Alert>
          )}

          {!!createService.error && <Alert severity="error">{createService.error.message}</Alert>}
        </Stack>
      </DialogContent>
      {createService.isPending && <LinearProgress />}
      <DialogActions>
        <Button onClick={handleClose} disabled={createService.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!isValid || createService.isPending}>
          Create service
        </Button>
      </DialogActions>
    </Dialog>
  );
}
